import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Typography from "@material-ui/core/Typography";
import SvgIcon from "@material-ui/core/SvgIcon";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import { useTranslation } from "react-i18next";

import { ReactComponent as Tool1 } from "assets/icons/tool1.svg";
import { ReactComponent as Tool2 } from "assets/icons/tool2.svg";
import { ReactComponent as Tool3 } from "assets/icons/tool3.svg";
import { ReactComponent as Tool4 } from "assets/icons/tool4.svg";
import { ReactComponent as Tool5 } from "assets/icons/tool5.svg";
import { ReactComponent as Module } from "assets/icons/modicon.svg";

const useStyles = makeStyles({
  card: {
    marginBottom: 12,
    position: "relative",
  },
  cardContent: {
    paddingBottom: 8,
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: 12,
  },
  icon: {
    fontSize: 36,
    marginRight: 12,
  },
  quantityContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 8,
  },
  quantityControls: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  priceInfo: {
    marginTop: 8,
  },
  cardActions: {
    padding: "8px 16px",
    paddingTop: 0,
  },
});

interface CartCardProps {
  item: string;
  quantity: number;
  onRemove?: () => void;
  onIncrement?: () => void;
  onDecrement?: () => void;
}

export default function CartCard({
  item,
  quantity,
  onRemove,
  onIncrement,
  onDecrement,
}: CartCardProps): React.ReactElement {
  const classes = useStyles();
  const { t } = useTranslation();

  // Determine color based on item type and number
  let color;
  if (item[0] === "m") {
    const moduleNum = item[6];
    const colorMap = {
      "1": "#bb3737",
      "2": "#ef7e28",
      "3": "#0c8197",
      "4": "#098359",
      "5": "#7f4a7f",
    };
    color = colorMap[moduleNum as keyof typeof colorMap];
  } else if (item[0] === "t") {
    const toolNum = item[4];
    const colorMap = {
      "1": "#bb3737",
      "2": "#ef7e28",
      "3": "#0c8197",
      "4": "#098359",
      "5": "#7f4a7f",
    };
    color = colorMap[toolNum as keyof typeof colorMap];
  }

  // Determine icon and price
  let icon: React.ElementType | undefined;
  let price: number | undefined;
  if (item[0] === "m") {
    icon = Module;
    price = 50;
  } else if (item[0] === "t") {
    price = 20;
    const toolNum = item[4];
    const iconMap = {
      "1": Tool1,
      "2": Tool2,
      "3": Tool3,
      "4": Tool4,
      "5": Tool5,
    };
    icon = iconMap[toolNum as keyof typeof iconMap];
  }

  const subtotal = quantity * price!;

  return (
    <Card className={classes.card} variant="outlined">
      <CardContent className={classes.cardContent}>
        <div className={classes.header}>
          <SvgIcon
            component={icon as React.ElementType}
            className={classes.icon}
            style={{ color: color }}
          />
          <Typography variant="h6" style={{ flex: 1, fontWeight: 500 }}>
            {t(`assignments:assessmentNames.${item}`)}
          </Typography>
        </div>

        <div className={classes.priceInfo}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="caption" style={{ color: "#666" }}>
                {t("shop:cart.pricePerUnit")}
              </Typography>
              <Typography variant="body1" style={{ fontWeight: 600 }}>
                ${price}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" style={{ color: "#666" }}>
                {t("shop:cart.subtotal")}
              </Typography>
              <Typography variant="body1" style={{ fontWeight: 600, color: "#006EB3" }}>
                ${subtotal}
              </Typography>
            </Grid>
          </Grid>
        </div>

        <div className={classes.quantityContainer}>
          <Typography variant="caption" style={{ color: "#666" }}>
            {t("shop:cart.quantity")}
          </Typography>
          <div className={classes.quantityControls}>
            {quantity > 1 && (
              <IconButton
                size="small"
                onClick={onDecrement}
                style={{ border: "1px solid #ccc" }}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
            )}
            <Typography variant="body1" style={{ minWidth: 30, textAlign: "center", fontWeight: 600 }}>
              {quantity}
            </Typography>
            <IconButton
              size="small"
              onClick={onIncrement}
              style={{ border: "1px solid #ccc" }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      </CardContent>

      <CardActions className={classes.cardActions}>
        <Button
          fullWidth
          variant="outlined"
          color="secondary"
          startIcon={<DeleteIcon />}
          onClick={onRemove}
          style={{ textTransform: "none" }}
        >
          {t("shop:cart.remove")}
        </Button>
      </CardActions>
    </Card>
  );
}
